import { useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, LayoutDashboard } from 'lucide-react';
import { PluginPageHeader } from '@/app/components/PluginPageHeader';
import { GovernanceStatCards } from './GovernanceStatCards';
import { ComplianceScorecard } from './ComplianceScorecard';
import { ActivityTimeline } from './ActivityTimeline';
import { CurationDonutCharts } from './CurationDonutCharts';
import { AuditLogPage } from '@/app/features/audit-log/AuditLogPage';
import { ApprovalsTab } from './ApprovalsTab';
import { PoliciesTab } from './PoliciesTab';
import { useGovernanceStore } from './useGovernanceStore';
import { useApiToken } from '@/auth/hooks';

export function GovernancePage() {
  const { getAccessToken } = useApiToken();
  const { stats, compliance, dashboardLoading, loadDashboard } = useGovernanceStore();

  useEffect(() => {
    void loadDashboard(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingCount = stats?.pendingApprovals ?? 0;

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
      <PluginPageHeader group="admin" title="Sopho Governance" icon={ShieldCheck} />

      <GovernanceStatCards stats={stats} loading={dashboardLoading} />

      <Tabs defaultValue="dashboard">
          <TabsList className="h-9">
            <TabsTrigger value="dashboard" className="gap-1 px-3 text-xs">
              <LayoutDashboard className="h-3 w-3" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="audit-log" className="px-3 text-xs">Audit-Log</TabsTrigger>
            <TabsTrigger value="approvals" className="px-3 text-xs">
              Approvals
              {pendingCount > 0 && (
                <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[10px]">{pendingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="policies" className="px-3 text-xs">Policies</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-4 space-y-6">
            <ComplianceScorecard compliance={compliance} loading={dashboardLoading} />
            <CurationDonutCharts />
            <ActivityTimeline stats={stats} loading={dashboardLoading} />
          </TabsContent>

          <TabsContent value="audit-log" className="mt-4">
            <AuditLogPage embedded />
          </TabsContent>

          <TabsContent value="approvals" className="mt-4">
            <ApprovalsTab />
          </TabsContent>

          <TabsContent value="policies" className="mt-4">
            <PoliciesTab />
          </TabsContent>
      </Tabs>
    </div>
  );
}
